import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import Typography from "@/components/ui/typography";
import { cn } from "@/lib/utils";

import { Thread } from "../types/thread";
import { NewThreadButton } from "./NewThreadButton";
import { ThreadsList } from "./ThreadsList";

interface ThreadsSidebarProps {
  threads: Thread[];
  activeThreadId: string | null;
  onThreadSelect: (threadId: string) => void;
  onThreadDelete: (threadId: string) => void;
  onNewThread: () => void;
  isLoading?: boolean;
  isMobile?: boolean;
}

export const ThreadsSidebar = ({
  threads,
  activeThreadId,
  onThreadSelect,
  onThreadDelete,
  onNewThread,
  isLoading = false,
  isMobile = false,
}: ThreadsSidebarProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Mobile mode: render just the content without wrapper positioning
  if (isMobile) {
    return (
      <div className="flex flex-col h-full bg-sds-neutral-00">
        {/* Header with New Thread Button */}
        <div className="px-4 pt-4 pb-3 flex items-center justify-between gap-3">
          <Typography size="l3" className="text-sds-neutral-80 flex-shrink-0">
            Chats
          </Typography>
          <div className="min-w-0">
            <NewThreadButton onClick={onNewThread} disabled={isLoading} />
          </div>
        </div>

        {/* Threads List */}
        <div className="flex-1 overflow-hidden pb-4 min-w-0">
          <ThreadsList
            threads={threads}
            activeThreadId={activeThreadId}
            onThreadSelect={onThreadSelect}
            onThreadDelete={onThreadDelete}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative bg-sds-neutral-00 border-r border-sds-neutral-20 transition-all duration-300 flex flex-col pt-20",
        isCollapsed ? "w-0 md:w-16" : "w-80",
      )}
    >
      {/* Collapse/Expand Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-[100px] z-10 bg-white border border-sds-neutral-20 rounded-full p-1 shadow-subtle hover:shadow-elevated transition-all"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4 text-sds-neutral-80" />
        ) : (
          <ChevronLeft className="h-4 w-4 text-sds-neutral-80" />
        )}
      </button>

      {!isCollapsed && (
        <>
          {/* Header with New Thread Button */}
          <div className="px-4 pt-4 pb-3 flex items-center justify-between gap-3">
            <Typography size="l3" className="text-sds-neutral-80 flex-shrink-0">
              Chats
            </Typography>
            <div className="min-w-0">
              <NewThreadButton onClick={onNewThread} disabled={isLoading} />
            </div>
          </div>

          {/* Threads List */}
          <div className="flex-1 overflow-hidden pb-4 min-w-0">
            <ThreadsList
              threads={threads}
              activeThreadId={activeThreadId}
              onThreadSelect={onThreadSelect}
              onThreadDelete={onThreadDelete}
              isLoading={isLoading}
            />
          </div>
        </>
      )}

      {isCollapsed && (
        <div className="hidden md:flex flex-col items-center py-6 px-2">
          <button
            onClick={onNewThread}
            disabled={isLoading}
            className="p-2 rounded-lg border border-[#e8e8e8] text-[#1e1e24] hover:bg-[#f9f9f9] transition-colors"
            aria-label="New chat"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
